import { useTranslation } from "react-i18next";
import { ScheduleEntry } from "@/types/tour";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Footprints, Clock, Star, MapPin, AlertTriangle } from "lucide-react";

interface TourTimelineProps {
  schedule: ScheduleEntry[];
}

function parseOpeningHoursWarning(
  openingHours: string[] | undefined,
  closedLabel: string,
  open24Label: string
): { text: string; isOpen: boolean } | null {
  if (!openingHours || openingHours.length === 0) return null;

  const dayText = openingHours[0];
  if (!dayText) return null;

  const colonIdx = dayText.indexOf(":");
  if (colonIdx === -1) return { text: dayText, isOpen: true };

  const hoursStr = dayText.substring(colonIdx + 1).trim();
  const lower = hoursStr.toLowerCase();

  if (lower.includes("closed") || lower.includes("geschlossen")) {
    return { text: closedLabel, isOpen: false };
  }

  if (lower.includes("open 24") || lower.includes("24 stunden") || lower.includes("24 hours")) {
    return { text: open24Label, isOpen: true };
  }

  return { text: hoursStr, isOpen: true };
}

export function TourTimeline({ schedule }: TourTimelineProps) {
  const { t } = useTranslation();

  const typeLabels: Record<string, string> = {
    bar: t("timeline.type_bar"),
    restaurant: t("timeline.type_restaurant"),
    kiosk: t("timeline.type_kiosk"),
    gas_station: t("timeline.type_gas_station"),
    supermarket: t("timeline.type_supermarket"),
    liquor_store: t("timeline.type_liquor_store"),
    other: t("timeline.type_other"),
  };

  return (
    <div className="space-y-0">
      <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
        <Clock className="h-5 w-5 text-primary" />
        {t("timeline.title")}
      </h3>

      {schedule.map((entry, index) => {
        const hoursInfo = parseOpeningHoursWarning(
          entry.bar.openingHours,
          t("timeline.closed"),
          t("timeline.open_24h")
        );

        return (
          <div key={index}>
            <Card className="relative border-border/50 bg-card p-3 sm:p-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-base sm:text-lg font-bold text-primary-foreground">
                  {index + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2">
                    <div className="min-w-0">
                      <h4 className="font-semibold break-words">{entry.bar.name}</h4>
                      <p className="flex items-start gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                        <span className="break-words min-w-0">{entry.bar.address}</span>
                      </p>
                    </div>
                    <div className="flex-shrink-0 sm:text-right">
                      <p className="text-sm font-medium text-primary">{entry.arrivalTime}</p>
                      <p className="text-xs text-muted-foreground">{t("timeline.until")} {entry.departureTime}</p>
                    </div>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {entry.bar.rating > 0 && (
                      <span className="flex items-center gap-1">
                        <Star className="h-3 w-3 text-primary" />
                        {entry.bar.rating.toFixed(1)}
                      </span>
                    )}
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-secondary-foreground">
                      {typeLabels[entry.bar.type] || entry.bar.type}
                    </span>
                  </div>

                  {hoursInfo && (
                    <div className="mt-2">
                      {hoursInfo.isOpen ? (
                        <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                          <Clock className="mr-1 h-3 w-3" />
                          {hoursInfo.text}
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs">
                          <AlertTriangle className="mr-1 h-3 w-3" />
                          {hoursInfo.text}
                        </Badge>
                      )}
                    </div>
                  )}

                  {entry.bar.warning && (
                    <div className="mt-2">
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="mr-1 h-3 w-3" />
                        {entry.bar.warning}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {entry.walkingToNext && (
              <div className="flex items-center gap-3 py-2 pl-5">
                <div className="flex h-6 w-6 items-center justify-center">
                  <Footprints className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="h-px flex-1 bg-border" />
                <p className="text-xs text-muted-foreground">
                  🚶 {entry.walkingToNext.durationMinutes} min ({Math.round(entry.walkingToNext.distanceMeters)}m)
                </p>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
